import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  Calendar,
  Clock,
  Tag,
  ArrowLeft,
  ArrowRight,
  Share,
} from "lucide-react";
import { blogPosts } from "@/data";
import { formatDate } from "@/lib/utils";
import { addNotification } from "@/store";
import { JSX } from "react/jsx-runtime";

export const Route = createFileRoute("/_public/blog/$slug")({
  loader: ({ params }) => {
    const post = blogPosts.find((p) => p.slug === params.slug);
    if (!post) throw notFound();
    const currentIndex = blogPosts.indexOf(post);
    const prev = blogPosts[currentIndex - 1] ?? null;
    const next = blogPosts[currentIndex + 1] ?? null;
    return { post, prev, next };
  },
  component: BlogPostPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function BlogPostPage(): JSX.Element {
  const { post, prev, next } = Route.useLoaderData();

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      addNotification("success", "Article link copied to clipboard!");
    });
  };

  return (
    <>
      <Helmet>
        <title>{post.metaTitle}</title>
        <meta name="description" content={post.metaDescription} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:type" content="article" />
        <link
          rel="canonical"
          href={`https://rosewoodtax.co.ke/blog/${post.slug}`}
        />
      </Helmet>

      <div className="bg-cream py-4">
        <div className="max-w-7xl mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-neutral-800">
            <Link to="/" className="hover:text-burgundy transition-colors">
              Home
            </Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-burgundy transition-colors">
              Blog
            </Link>
            <span>/</span>
            <span className="text-[#1A1A1A] font-medium line-clamp-1">
              {post.title}
            </span>
          </nav>
        </div>
      </div>

      <article className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className=" text-4xl md:text-5xl font-semibold text-[#1A1A1A] leading-tight mb-6">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-6 text-sm text-neutral-800 mb-8 pb-8 border-b border-[#E8E0D0]">
              {/* <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 bg-burgundy rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">
                    {post.author.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")}
                  </span>
                </div>
                <div>
                  <p className="text-[#1A1A1A] font-medium text-xs">
                    {post.author.name}
                  </p>
                  <p className="text-neutral-800 text-[11px]">
                    {post.author.role}
                  </p>
                </div>
              </div> */}
              <span className="flex items-center gap-1.5">
                <Calendar size={13} />
                {formatDate(post.publishedAt)}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock size={13} />
                {post.readTime} min read
              </span>
              <button
                onClick={handleShare}
                className="ml-auto flex items-center gap-1.5 px-3 py-1.5 border border-[#E8E0D0] rounded-lg hover:border-[#6B1E2A]/30 hover:text-burgundy transition-colors"
              >
                <Share size={13} />
                Share
              </button>
            </div>
          </motion.div>

          <div className="aspect-[16/7] bg-burgundy/8 rounded-2xl mb-10 overflow-hidden flex items-center justify-center">
            {post.coverImage ? (
              <img
                src={post.coverImage}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="text-center">
                <div className="w-20 h-20 bg-burgundy/15 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-4xl font-bold text-burgundy/30">
                    {post.category.charAt(0)}
                  </span>
                </div>
                <p className="text-sm text-neutral-800/50">{post.category}</p>
              </div>
            )}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="prose prose-lg max-w-none"
          >
            {post.content.split("\n\n").map((section, i) => {
              if (section.startsWith("## ")) {
                return (
                  <h2
                    key={i}
                    className=" text-2xl font-semibold text-[#1A1A1A] mt-10 mb-4"
                  >
                    {section.replace("## ", "")}
                  </h2>
                );
              }
              if (section.startsWith("### ")) {
                return (
                  <h3
                    key={i}
                    className=" text-xl font-semibold text-[#1A1A1A] mt-8 mb-3"
                  >
                    {section.replace("### ", "")}
                  </h3>
                );
              }
              return (
                <p key={i} className="text-neutral-800 leading-relaxed mb-5">
                  {section}
                </p>
              );
            })}
          </motion.div>

          <div className="mt-10 pt-8 border-t border-[#E8E0D0]">
            <div className="flex flex-wrap items-center gap-2">
              <Tag size={14} className="text-neutral-800" />
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-cream border border-[#E8E0D0] text-xs text-neutral-800 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* <div className="mt-10 p-7 bg-cream rounded-2xl">
            <div className="flex items-start gap-5">
              <div className="w-14 h-14 bg-burgundy rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-lg">
                  {post.author.name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")}
                </span>
              </div>
              <div>
                <p className="font-semibold text-[#1A1A1A] text-lg">
                  {post.author.name}
                </p>
                <p className="text-sm text-burgundy font-medium mb-2">
                  {post.author.role}
                </p>
                <p className="text-sm text-neutral-800 leading-relaxed">
                  {post.author.bio}
                </p>
              </div>
            </div>
          </div> */}

          <div className="mt-10 grid grid-cols-2 gap-4">
            {prev ? (
              <Link
                to={`/blog/${prev.slug}` as never}
                className="group flex items-start gap-3 p-5 bg-cream rounded-xl hover:shadow-xs transition-all"
              >
                <ArrowLeft
                  size={18}
                  className="text-neutral-800 mt-0.5 group-hover:text-burgundy transition-colors flex-shrink-0"
                />
                <div>
                  <p className="text-xs text-neutral-800 mb-1">Previous</p>
                  <p className="text-sm font-medium text-[#1A1A1A] line-clamp-2 group-hover:text-burgundy transition-colors">
                    {prev.title}
                  </p>
                </div>
              </Link>
            ) : (
              <div />
            )}
            {next && (
              <Link
                to={`/blog/${next.slug}` as never}
                className="group flex items-end gap-3 p-5 bg-cream rounded-xl hover:shadow-xs transition-all text-right"
              >
                <div>
                  <p className="text-xs text-neutral-800 mb-1">Next</p>
                  <p className="text-sm font-medium text-[#1A1A1A] line-clamp-2 group-hover:text-burgundy transition-colors">
                    {next.title}
                  </p>
                </div>
                <ArrowRight
                  size={18}
                  className="text-neutral-800 mt-0.5 group-hover:text-burgundy transition-colors flex-shrink-0"
                />
              </Link>
            )}
          </div>

          <div className="mt-8 text-center">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm font-semibold text-burgundy hover:text-[#4A1219] transition-colors"
            >
              <ArrowLeft size={14} />
              Back to All Articles
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}
